import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../lib/api';
import { subscribeRealtime } from '../lib/realtime';
import type {
  ActivityBotKindFilter,
  ActivityResponse,
  ActivityResultFilter,
  ActivityType,
} from '../lib/types';

const PAGE_SIZE = 20;

interface Filters {
  type: ActivityType;
  /** Comma-separated narrow list of ItemKind values (wallet sub-filter). */
  kinds?: string;
  bot_kind?: ActivityBotKindFilter;
  result?: ActivityResultFilter;
  /** ISO date strings (yyyy-MM-dd or full ISO). */
  from_ts?: string;
  to_ts?: string;
}

/**
 * Cursor-paginated infinite scroll for /v1/activity. The cursor is
 * opaque server-side state; we never inspect it on the client.
 *
 * Realtime: any incoming user:* event triggers an invalidate of the
 * active filter combination. Older pages stay valid (cursor is older-
 * than only, so newly-inserted events can't shift them) — a refetch
 * of the head is enough to surface the new event without flicker.
 */
export function useInfiniteActivity(filters: Filters) {
  const qc = useQueryClient();
  const queryKey = ['activity', filters] as const;

  const result = useInfiniteQuery<ActivityResponse, Error>({
    queryKey,
    initialPageParam: null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        type: filters.type,
        limit: String(PAGE_SIZE),
      });
      if (pageParam) params.set('cursor', String(pageParam));
      if (filters.kinds) params.set('kinds', filters.kinds);
      if (filters.bot_kind) params.set('bot_kind', filters.bot_kind);
      if (filters.result) params.set('result', filters.result);
      if (filters.from_ts) params.set('from_ts', filters.from_ts);
      if (filters.to_ts) params.set('to_ts', filters.to_ts);
      return api.get<ActivityResponse>(`/v1/activity?${params.toString()}`);
    },
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 15_000,
  });

  // Refresh paths: realtime (below) for live invalidation on
  // ``:trade`` / ``:wallet`` events, plus TanStack's
  // default ``refetchOnWindowFocus`` for the "user came back to the
  // tab" case. No periodic polling — the head-only fallback I tried
  // on PR #193 was redundant when realtime is healthy and just
  // burned RPS. If we see prod cases where realtime drops without
  // recovery, fix should land at the WS layer (reconnect /
  // resubscribe), not as a poll.
  useEffect(() => {
    const unsub = subscribeRealtime(
      (ch) => ch.startsWith('user:'),
      () => {
        qc.invalidateQueries({ queryKey });
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  return result;
}
